import { useState } from 'react';
import { Bar, Card, Row } from '../components/Card';
import { TimeSeries } from '../components/TimeSeries';
import type { Series } from '../components/TimeSeries';
import * as fmt from '../format';
import type { Store } from '../store';

/// How far back the charts look. A three-hundred-day walk squeezed into six
/// hundred pixels is a texture rather than a chart, so the default is the
/// stretch you can still act on.
const RANGES: Array<{ label: string; days: number | null }> = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '6 months', days: 182 },
  { label: 'All', days: null },
];

export function Progress({ store }: { store: Store }) {
  const { summary, days } = store;
  const [range, setRange] = useState<number | null>(90);
  if (!summary) return <p className="muted">{store.loading ? 'Loading…' : 'No data yet.'}</p>;

  const all = [...days].reverse();
  const chronological = range === null ? all : all.slice(Math.max(all.length - range, 0));

  return (
    <>
      <Card wide>
        <div className="headline headline--split">
          <p className="headline__value">{fmt.percent(summary.pctComplete)}</p>
          <p className={summary.currentDiffMiles >= 0 ? 'good' : 'warn'}>
            {fmt.signedMiles(summary.currentDiffMiles)}
          </p>
        </div>
        <Bar fraction={summary.pctComplete} />
        <Row label="Walked" value={fmt.miles(summary.totalActualMiles)} />
        <Row label="Remaining" value={fmt.miles(summary.remainingMiles)} />
        <Row label="Total steps" value={fmt.steps(summary.totalSteps)} />
        <Row label="Average per day" value={fmt.miles(summary.currentAveragePerDayMiles)} />
        <Row label="Target per day" value={fmt.miles(summary.targetPerDayMiles)} />
      </Card>

      <div className="ranges" role="group" aria-label="Chart range">
        {RANGES.map((option) => (
          <button
            key={option.label}
            type="button"
            className={range === option.days ? 'ranges__on' : undefined}
            aria-pressed={range === option.days}
            onClick={() => setRange(option.days)}
          >
            {option.label}
          </button>
        ))}
        <span className="muted">{chronological.length} days shown</span>
      </div>

      <Card title="Distance against the plan" wide>
        <TimeSeries rows={chronological} series={distanceSeries} height={240} />
      </Card>

      <div className="grid">
        <Card title="Ahead or behind">
          <TimeSeries rows={chronological} series={diffSeries} zeroLine />
        </Card>
        <Card title="Each day">
          <TimeSeries
            rows={chronological}
            series={dailySeries(summary.targetPerDayMiles)}
            zeroLine
          />
        </Card>
        <Card title="Share of the route">
          <TimeSeries rows={chronological} series={percentSeries} />
        </Card>
        <Card title="Where each day ranked">
          <TimeSeries rows={chronological} series={rankSeries} invert />
        </Card>

        <Card title="Projection">
          <Row label="Days elapsed" value={String(summary.daysElapsed)} />
          <Row label="Days recorded" value={String(summary.recordedDays)} />
          <Row
            label="Estimated days left"
            value={Number.isFinite(summary.estimatedDaysLeft)
              ? String(Math.trunc(summary.estimatedDaysLeft)) : '—'}
          />
          <Row
            label="Versus target"
            value={Number.isFinite(summary.estimatedVsTargetDays)
              ? `${Math.trunc(summary.estimatedVsTargetDays)} days` : '—'}
            tone={summary.estimatedVsTargetDays >= 0 ? 'good' : 'warn'}
          />
          <Row label="Estimated finish" value={fmt.mediumDate(summary.estimatedFinishDate)} />
          <Row label="Target finish" value={fmt.mediumDate(summary.targetFinishDate)} />
          <Row label="Final day miles" value={fmt.miles(summary.estimatedMilesOnFinalDay)} />
        </Card>

        <Card title="Peaks">
          <Row label="Best day" value={fmt.miles(summary.maxMilesPerDay)} />
          {summary.maxDistanceAheadMiles !== null && (
            <Row label="Furthest ahead" value={fmt.miles(summary.maxDistanceAheadMiles)} />
          )}
          {summary.maxDistanceBehindMiles !== null && (
            <Row label="Furthest behind" value={fmt.miles(summary.maxDistanceBehindMiles)} />
          )}
          {summary.behindMaxDifferenceMiles > 0 ? (
            <>
              <Row label="Below your peak" value={fmt.miles(summary.behindMaxDifferenceMiles)} />
              <Row label="Time to regain" value={fmt.duration(summary.timeToMaxMinutes)} />
            </>
          ) : (
            <Row label="Position" value="At your best" />
          )}
        </Card>

        {summary.nextPercentage && (
          <Card title={`Next ${fmt.percent(summary.nextPercentage.percentage, 0)}`}>
            <Row label="At" value={fmt.miles(summary.nextPercentage.targetDistanceMiles)} />
            <Row label="To go" value={fmt.miles(summary.nextPercentage.differenceMiles)} />
            <Row
              label={fmt.paceLabel(summary.paceBasis)}
              value={fmt.duration(summary.nextPercentage.estimatedTimeMinutes)}
            />
          </Card>
        )}
      </div>
    </>
  );
}

/// The spreadsheet's own columns, as charts. Nothing here is recomputed —
/// each series reads a figure the API already worked out.

// Whole miles: these run into the hundreds, where two decimal places are
// noise on an axis and no help in a tooltip.
const wholeMiles = (value: number) => fmt.miles(value, 0);

const distanceSeries: Series[] = [
  {
    key: 'actual', label: 'Walked', value: (r) => r.totalActualMiles,
    kind: 'line', tone: 1, format: wholeMiles,
  },
  {
    key: 'target', label: 'Plan', value: (r) => r.targetMiles,
    kind: 'line', tone: 2, format: wholeMiles,
  },
];

const diffSeries: Series[] = [
  {
    key: 'diff', label: 'Miles ahead', value: (r) => r.diffMiles,
    kind: 'area', tone: 1, format: fmt.signedMiles,
  },
  {
    key: 'change', label: 'Day on day', value: (r) => r.diffChangeMiles,
    kind: 'line', tone: 3, format: fmt.signedMiles, hiddenByDefault: true,
  },
];

const dailySeries = (target: number): Series[] => [
  { key: 'miles', label: 'Miles walked', value: (r) => r.miles, kind: 'bar', tone: 1 },
  { key: 'target', label: 'Daily target', value: () => target, kind: 'line', tone: 2 },
  {
    key: 'steps', label: 'Steps', value: (r) => r.steps ?? 0,
    kind: 'line', tone: 3, format: fmt.steps, hiddenByDefault: true,
  },
];

const percentSeries: Series[] = [
  {
    key: 'complete', label: 'Complete', value: (r) => r.pctComplete,
    kind: 'line', tone: 1, format: (v) => fmt.percent(v, 0),
  },
  {
    key: 'plan', label: 'Plan', value: (r) => r.targetPct,
    kind: 'line', tone: 2, format: (v) => fmt.percent(v, 0),
  },
];

const rankSeries: Series[] = [
  {
    key: 'rank', label: 'Rank that day', value: (r) => r.ranking,
    kind: 'line', tone: 4, format: (v) => `#${Math.round(v)}`,
  },
];
